import type { FC } from "react";
import { useState } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

export interface ICitationCardData {
    icon?: string;
    source: string;
    title: string;
    description: string;
    url: string;
}

interface ICitationCardProps {
    citation: ICitationCardData;
    onClick?: () => void;
}

export const CitationCard: FC<ICitationCardProps> = ({ citation, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <Flex
            flexDirection="column"
            padding="16px"
            background={isHovered ? colorsPalettes.carbon[25] : colorsPalettes.carbon[0]}
            border={`1px solid ${colorsPalettes.carbon[50]}`}
            borderRadius="8px"
            gap="8px"
            maxHeight="128px"
            width="100%"
            minWidth="0"
            cursor={onClick ? "pointer" : "default"}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={
                onClick
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onClick();
                          }
                      }
                    : undefined
            }
        >
            <Flex
                alignItems="center"
                gap="8px"
            >
                {citation.icon ? (
                    <Flex
                        width="24px"
                        height="24px"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="4px"
                        overflow="hidden"
                    >
                        <img
                            src={citation.icon}
                            alt={citation.source}
                            width="16"
                            height="16"
                            style={{ objectFit: "contain" }}
                        />
                    </Flex>
                ) : null}
                <Flex
                    flex="1"
                    alignItems="center"
                    justifyContent="space-between"
                    minWidth="0"
                >
                    <Text
                        fontVariant="input2"
                        color={colorsPalettes.carbon[500]}
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                    >
                        {citation.source}
                    </Text>
                    {onClick ? (
                        <SWReactIcons
                            iconName="link-out"
                            size="xs"
                            iconColor={isHovered ? colorsPalettes.blue[400] : colorsPalettes.carbon[400]}
                        />
                    ) : null}
                </Flex>
            </Flex>
            <Text
                dir="auto"
                textAlign="initial"
                fontVariant="subtitle2"
                color={colorsPalettes.carbon[500]}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
            >
                {citation.title}
            </Text>
            <Text
                dir="auto"
                textAlign="initial"
                fontVariant="body2"
                color={colorsPalettes.carbon[400]}
                overflow="hidden"
                style={{
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                }}
            >
                {citation.description}
            </Text>
        </Flex>
    );
};
